import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { logger } from '../../services/loggingService';
import {
  ClipboardList,
  Layout,
  BarChart,
  Bot,
  TrendingUp,
  Plug,
  CreditCard,
  PoundSterling,
  Settings,
  Home,
  ChevronLeft,
  ChevronRight,
  Users,
  Code,
  Building2,
} from 'lucide-react';
import { CycloneLogo } from '../CycloneLogo';
import type { NavSection } from '../../types';

interface LeftNavProps {
  activeSection: NavSection;
  onSectionChange: (section: NavSection) => void;
  projectId?: string | null; // Reserved for future use
}

const navItems: {
  id: NavSection;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
  requiresProject?: boolean;
  dividerAfter?: boolean;
}[] = [
  // Core Workflow
  { id: 'welcome', label: 'Dashboard', icon: Home },
  {
    id: 'blueprint',
    label: 'Blueprint',
    icon: ClipboardList,
  },
  { id: 'ide', label: 'IDE', icon: Code },
  {
    id: 'jira',
    label: 'Tickets',
    icon: Layout,
    dividerAfter: true,
  },

  // Configuration
  { id: 'agents', label: 'Agents', icon: Bot },
  { id: 'integrations', label: 'Integrations', icon: Plug, dividerAfter: true },

  // Insights & Account
  { id: 'reports', label: 'Reports', icon: BarChart },
  { id: 'usage', label: 'Usage', icon: TrendingUp },
  { id: 'pricing', label: 'Pricing', icon: PoundSterling },
  { id: 'billing', label: 'Billing', icon: CreditCard },
  { id: 'organization', label: 'Organization', icon: Building2, dividerAfter: true },

  // Admin Only
  { id: 'users', label: 'User Management', icon: Users, adminOnly: true },
  { id: 'admin', label: 'System Settings', icon: Settings, adminOnly: true },
];

export function LeftNav({
  activeSection,
  onSectionChange,
  projectId: _projectId, // Prefix with _ to indicate intentionally unused
}: LeftNavProps) {
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    checkSuperAdmin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const checkSuperAdmin = async () => {
    if (!user) return;
    try {
      // Check if user has super_admin role from auth metadata
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();
      const isSuperAdmin = authUser?.app_metadata?.role === 'super_admin';
      setIsSuperAdmin(isSuperAdmin);
    } catch (error) {
      logger.error('Error checking super admin status', { error, userId: user?.id });
      setIsSuperAdmin(false);
    }
  };

  return (
    <div
      className={`sidebar bg-gray-900 flex flex-col py-6 transition-all duration-300 ease-in-out relative ${
        isExpanded ? 'w-64' : 'w-20'
      }`}
    >
      <div className='flex items-center justify-center mb-6 flex-shrink-0 px-4'>
        {isExpanded ? (
          <div className='flex items-center gap-3'>
            <CycloneLogo size={40} />
            <span className='text-white font-bold text-xl'>Cyclone AI</span>
          </div>
        ) : (
          <CycloneLogo size={40} />
        )}
      </div>

      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className='absolute -right-3 top-8 w-6 h-6 bg-gray-800 border border-gray-700 rounded-full flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 transition-colors z-10'
        title={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
        aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
        aria-expanded={isExpanded}
      >
        {isExpanded ? (
          <ChevronLeft className='w-4 h-4' />
        ) : (
          <ChevronRight className='w-4 h-4' />
        )}
      </button>

      <nav
        className='flex-1 overflow-y-auto px-3 pb-4 custom-scrollbar'
        role='navigation'
        aria-label='Main navigation'
      >
        <div className='flex flex-col gap-1' role='menu'>
          {navItems.map((item, index) => {
            // Hide admin-only items for non-admins
            if (item.adminOnly && !isSuperAdmin) return null;

            const Icon = item.icon;
            const isActive = activeSection === item.id;
            const showDivider =
              item.dividerAfter && index < navItems.length - 1;

            return (
              <div key={item.id}>
                <button
                  onClick={() => onSectionChange(item.id)}
                  className={`sidebar-item flex items-center gap-3 p-3 rounded-lg transition-all flex-shrink-0 w-full ${
                    isActive
                      ? 'sidebar-item-active bg-accent-500 text-white shadow-lg'
                      : 'text-gray-400 hover:text-white hover:bg-gray-800'
                  } ${isExpanded ? 'justify-start' : 'justify-center'}`}
                  title={!isExpanded ? item.label : undefined}
                  aria-label={`Navigate to ${item.label}`}
                  aria-current={isActive ? 'page' : undefined}
                  role='menuitem'
                >
                  <Icon className='w-5 h-5 flex-shrink-0' />
                  {isExpanded && (
                    <span className='text-sm font-medium whitespace-nowrap flex-1 text-left'>
                      {item.label}
                    </span>
                  )}
                </button>
                {showDivider && <div className='h-px bg-gray-700 my-2 mx-3' />}
              </div>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
